import * as React from 'react';

import {
  Badge,
  Col,
  Popover,
  PopoverTrigger,
  Row,
  SearchField,
  cn,
  scrollIntoView,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideLoader2 } from 'lucide-react';

import { ParticipantCandidate } from '../_state/_types';
import * as contactsState from '../_state/contacts.state';
import { $addParticipant } from '../_state/conversation.state';
import { useTranslations } from '../i18n/useTranslations';

export type ContactRowProps = {
  contact: ParticipantCandidate;
  onClick: (contact: ParticipantCandidate) => void;
  preventHovering?: boolean;
  focused?: boolean;
  onMouseEnter: () => void;
};

export const ContactRow = ({
  contact,
  onClick,
  preventHovering,
  focused,
  onMouseEnter,
}: ContactRowProps) => {
  const t = useTranslations();

  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (focused) {
      scrollIntoView({ element: ref.current, gutter: 4 });
    }
  }, [focused]);

  return (
    <Col
      ref={ref}
      className={cn(
        '@ContactRow cursor-pointer rounded px-3 py-1 text-sm',
        !preventHovering && 'hover:bg-neutral/7',
        focused && 'bg-neutral/7',
      )}
      onClick={() => onClick(contact)}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onMouseEnter={onMouseEnter}
    >
      <Row gap1 xBetween>
        <span>{contact.name}</span>
        <Badge color={contact.type === 'internal' ? 'primary' : 'warning'}>
          {contact.type === 'internal'
            ? t.participantPopover.internal
            : t.participantPopover.external}
        </Badge>
      </Row>
      <Row className={'w-full text-current/50'}>
        <div className={'w-0 grow truncate'}>{contact.organizationName}</div>
      </Row>
    </Col>
  );
};

export type NewParticipantPopoverProps = { children: React.ReactNode };

export const NewParticipantPopover = (props: NewParticipantPopoverProps) => {
  const t = useTranslations();

  const [isOpen, setIsOpen] = React.useState(false);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);

  const [searchTerm, setSearchTerm] = useAtom(
    contactsState.$contactsSearchTerm.value,
  );

  const [preventHovering, setPreventHovering] = React.useState(false);

  const filteredContactList = useAtomValue(contactsState.$filteredContactList);
  const addParticipant = useSetAtom($addParticipant);

  const handleContactClick = React.useCallback(
    (contact: ParticipantCandidate) => {
      setIsOpen(false);
      setSearchTerm('');
      setFocusedIndex(-1);
      addParticipant(contact);
    },
    [addParticipant, setSearchTerm],
  );

  const handleSearchKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      setPreventHovering(true);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(
            Math.min(
              focusedIndex + 1,
              (filteredContactList.data?.length ?? 0) - 1,
            ),
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(Math.max(focusedIndex - 1, 0));
          break;

        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0) {
            const contact = filteredContactList.data?.[focusedIndex];
            if (contact) {
              handleContactClick(contact);
            }
          }
          break;
      }
    },
    [filteredContactList.data, focusedIndex, handleContactClick],
  );

  React.useEffect(() => {
    setFocusedIndex(0);
  }, [filteredContactList.data]);

  return (
    <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {props.children}
      <Popover>
        <Col className={'w-[300px]'}>
          <div className={'border-b border-neutral/20 p-3'}>
            <SearchField
              placeholder={t.participantPopover.searchContact}
              autoFocus
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              className={cn(
                filteredContactList.isFetching && '[&_svg]:animate-spin',
              )}
              icon={
                filteredContactList.isFetching ? <LucideLoader2 /> : undefined
              }
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          <Col
            className={'max-h-[300px] overflow-auto p-1'}
            onMouseMove={() => {
              setPreventHovering(false);
            }}
          >
            {filteredContactList.data?.map((contact, index) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                focused={focusedIndex === index}
                onClick={handleContactClick}
                preventHovering={preventHovering}
                onMouseEnter={() => setFocusedIndex(index)}
              />
            ))}
            {!filteredContactList.isLoading &&
              !filteredContactList.data?.length && (
                <div className={'p-3 text-center text-sm text-current/50'}>
                  {t.participantPopover.noContactsFound}
                </div>
              )}
          </Col>
        </Col>
      </Popover>
    </PopoverTrigger>
  );
};
